/**
 * Who we are, on paper.
 *
 * On an inflow deal DirectAuto is the buyer, so the buyer block on the deal is our own
 * company. These details go on a Vehicle Purchase Agreement and, downstream, on a tax invoice - an
 * ABN typed by hand onto one of those is a compliance problem nobody notices until an
 * accountant does.
 *
 * They live in admin_settings rather than in a component so that a change of address,
 * or a renewed dealer licence, is a row edit rather than a deploy.
 */
import { supabase } from '../lib/supabase'

export interface CompanyIdentity {
  name: string
  abn: string
  lmct: string
  address: string
  email: string
}

/* Held for the life of the page. It changes about once a year, and re-reading it on
   every deal opened would be a query per click for no benefit. */
let cached: CompanyIdentity | null = null

export async function loadCompanyIdentity(): Promise<CompanyIdentity | null> {
  if (cached) return cached

  try {
    const { data, error } = await supabase
      .from('admin_settings')
      .select('company_name, company_abn, company_lmct, company_address, company_email')
      .limit(1)
      .maybeSingle()

    if (error) throw error

    const row = (data || null) as Record<string, string | null> | null
    if (!row || !row.company_name) return null

    cached = {
      name: row.company_name || '',
      abn: row.company_abn || '',
      lmct: row.company_lmct || '',
      address: row.company_address || '',
      email: row.company_email || '',
    }
    return cached
  } catch (err) {
    // Not fatal: the panel simply does not prefill, and the person types it. Loud,
    // because silently blank company details on a purchase is worth knowing about.
    console.error('Could not read the company details from settings', err)
    return null
  }
}

/** Call after an admin edits the details, so the page stops serving the old ones. */
export function forgetCompanyIdentity(): void {
  cached = null
}
